import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
// `IconHorizontalDots` is the ui package's alias for the `IconDotGrid1x3Vertical` glyph, which is
// vertical despite the name. The row menu requires the vertical glyph, so alias it truthfully here
// (`IconThreeDots` is the horizontal one and must not be used in this row).
import { IconHorizontalDots as IconThreeDotsVertical } from '~/ui/core/icons';

import type { ProfileTaskSummary } from '../model/profile-task';
import { profileTaskTranslationKeys } from '../profile-task-translations';

type ProfileTaskRowTitleProps = {
    readonly isEditing: boolean;
    readonly isSelected: boolean;
    readonly onOpen: () => void;
    /** Without it the row offers no removal. */
    readonly onRemove: (() => void) | undefined;
    readonly summary: ProfileTaskSummary;
};

export const ProfileTaskRowTitle: FunctionComponent<ProfileTaskRowTitleProps> = ({
    isEditing,
    isSelected,
    onOpen,
    onRemove,
    summary,
}) => {
    const translate = useTranslate();
    const isGenerating = summary.generationState === 'queued' || summary.generationState === 'started';
    const canOpen = summary.generationState === 'finished';

    return (
        <div className="flex min-w-0 flex-1 items-center gap-xs">
            <Button
                className={`min-w-0 flex-1 rounded-md border-none bg-transparent px-xs py-xs text-left font-sans text-body-s leading-normal text-text-primary underline-offset-2 [overflow-wrap:anywhere] hover:underline ${isSelected ? 'underline' : ''}`}
                variant="plain"
                onClick={onOpen}
            >
                {summary.title}
            </Button>
            {isGenerating ? (
                <Badge data-testid="profile-task-generating" variant="gray">
                    {translate(profileTaskTranslationKeys.statusGenerating)}
                </Badge>
            ) : null}
            {summary.generationState === 'failed' ? (
                <span className="shrink-0 text-body-xs font-normal text-text-error" data-testid="profile-task-failed">
                    {translate(profileTaskTranslationKeys.statusGenerationFailed)}
                </span>
            ) : null}
            {isEditing && (canOpen || onRemove !== undefined) ? (
                <DropdownMenu>
                    <DropdownMenuTrigger>
                        <Button
                            aria-label={`${translate('common.options')}: ${summary.title}`}
                            iconOnly={true}
                            size="sm"
                            variant="tertiary"
                        >
                            <IconThreeDotsVertical size={16} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={5}>
                        {canOpen ? (
                            <DropdownMenuItem onSelect={onOpen}>
                                {translate(profileTaskTranslationKeys.editTask)}
                            </DropdownMenuItem>
                        ) : null}
                        {onRemove === undefined ? null : (
                            <DropdownMenuItem onSelect={onRemove}>
                                {translate(profileTaskTranslationKeys.removeTask)}
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : null}
        </div>
    );
};
